import { useState } from 'react'
import { GlassmorphicCard } from '@/components/ui/glassmorphic-card'
import { Plus, Pencil, Trash2, X } from 'lucide-react'
import { mockAbroadColleges, type AbroadCollege } from '@/data/admin-mock-data'

export function AdminAbroadCollegesView() {
    const [colleges, setColleges] = useState<AbroadCollege[]>(mockAbroadColleges)
    const [modalOpen, setModalOpen] = useState(false)
    const [editingId, setEditingId] = useState<string | null>(null)
    const [form, setForm] = useState({
        name: '', country: '', image: '', ranking: '', fees: '', intake: '',
        hostelFees: '', eligibility: '', teachingMedium: '', nmcStatus: '',
        brochureUrl: '', galleryImages: [] as string[],
        isActive: true
    })

    const openCreate = () => {
        setEditingId(null)
        setForm({
            name: '', country: '', image: '', ranking: '', fees: '', intake: '',
            hostelFees: '', eligibility: '', teachingMedium: '', nmcStatus: '',
            brochureUrl: '', galleryImages: [],
            isActive: true
        })
        setModalOpen(true)
    }

    const openEdit = (c: AbroadCollege) => {
        setEditingId(c.id)
        setForm({
            name: c.name, country: c.country, image: c.image, ranking: c.ranking, fees: c.fees, intake: c.intake,
            hostelFees: c.hostelFees || '', eligibility: c.eligibility || '', teachingMedium: c.teachingMedium || '',
            nmcStatus: c.nmcStatus || '', brochureUrl: c.brochureUrl || '', galleryImages: c.galleryImages || [],
            isActive: c.isActive
        })
        setModalOpen(true)
    }

    const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const url = URL.createObjectURL(e.target.files[0])
            setForm(f => ({ ...f, image: url }))
        }
    }

    const handleGalleryUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const urls = Array.from(e.target.files).map(file => URL.createObjectURL(file))
            setForm(f => ({ ...f, galleryImages: [...f.galleryImages, ...urls] }))
        }
    }

    const handleBrochureUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            // In a real app, upload to server. Here simulate with object URL.
            const url = URL.createObjectURL(e.target.files[0])
            setForm(f => ({ ...f, brochureUrl: url }))
        }
    }

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault()
        if (editingId) {
            setColleges(prev => prev.map(x => x.id === editingId ? { ...x, ...form } : x))
        } else {
            setColleges(prev => [...prev, { ...form, id: `ab-${Date.now()}` }])
        }
        setModalOpen(false)
    }

    const handleDelete = (id: string) => {
        if (confirm('Remove this college from the public listing?')) setColleges(prev => prev.filter(c => c.id !== id))
    }

    return (
        <div className="space-y-6 pb-24 lg:pb-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-theme-primary">Abroad Colleges</h2>
                    <p className="text-sm text-theme-secondary">Manage colleges shown on the Foreign Education page (public website)</p>
                </div>
                <button onClick={openCreate} className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary-blue hover:bg-blue-600 text-white font-semibold rounded-xl transition-colors">
                    <Plus className="h-5 w-5" />
                    Add College
                </button>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {colleges.map((c) => (
                    <GlassmorphicCard key={c.id} className="overflow-hidden">
                        <div className="relative h-36 overflow-hidden">
                            <img src={c.image} alt={c.name} className="w-full h-full object-cover" />
                            <div className="absolute top-2 left-2">
                                <span className="px-2 py-1 bg-primary-blue text-white text-xs font-medium rounded-full">{c.country}</span>
                            </div>
                            <div className="absolute top-2 right-2 flex gap-1">
                                <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 text-theme-primary"><Pencil className="h-4 w-4" /></button>
                                <button onClick={() => handleDelete(c.id)} className="p-1.5 rounded-lg bg-white/90 dark:bg-slate-800/90 text-red-600"><Trash2 className="h-4 w-4" /></button>
                            </div>
                        </div>
                        <div className="p-4">
                            <h3 className="font-semibold text-theme-primary mb-1">{c.name}</h3>
                            <p className="text-sm text-theme-secondary">{c.ranking} · {c.fees}</p>
                            <p className="text-xs text-theme-muted">Intake: {c.intake}</p>
                            <span className={`inline-block mt-2 px-2 py-1 rounded-full text-xs font-medium ${c.isActive ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' : 'bg-slate-200 dark:bg-slate-700 text-theme-secondary'}`}>
                                {c.isActive ? 'Visible' : 'Hidden'}
                            </span>
                        </div>
                    </GlassmorphicCard>
                ))}
            </div>

            {modalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setModalOpen(false)}>
                    <GlassmorphicCard className="w-full max-w-md p-6 shadow-xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-theme-primary">{editingId ? 'Edit College' : 'Add College'}</h3>
                            <button onClick={() => setModalOpen(false)} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"><X className="h-5 w-5" /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Name</label>
                                <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="College name" required />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Country</label>
                                <input value={form.country} onChange={e => setForm(f => ({ ...f, country: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="e.g. Russia" required />
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Image Upload</label>
                                    <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full text-sm text-theme-secondary file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary-blue/10 file:text-primary-blue hover:file:bg-primary-blue/20" />
                                    {form.image && <img src={form.image} alt="Preview" className="h-20 w-auto mt-2 rounded-lg object-cover" />}
                                    <input value={form.image} onChange={e => setForm(f => ({ ...f, image: e.target.value }))} className="w-full mt-2 px-3 py-1.5 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-xs text-theme-secondary" placeholder="Or Image URL" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Ranking</label>
                                    <input value={form.ranking} onChange={e => setForm(f => ({ ...f, ranking: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="e.g. #15 in Russia" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Tuition Fees</label>
                                    <input value={form.fees} onChange={e => setForm(f => ({ ...f, fees: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="₹4.5 Lakhs/year" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Hostel Fees</label>
                                    <input value={form.hostelFees} onChange={e => setForm(f => ({ ...f, hostelFees: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="₹50,000/year" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Intake</label>
                                    <input value={form.intake} onChange={e => setForm(f => ({ ...f, intake: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="September" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Medium of Teaching</label>
                                    <input value={form.teachingMedium} onChange={e => setForm(f => ({ ...f, teachingMedium: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="English" />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">Eligibility</label>
                                    <input value={form.eligibility} onChange={e => setForm(f => ({ ...f, eligibility: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary" placeholder="50% in PCB" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-theme-secondary mb-1">NMC Status</label>
                                    <select value={form.nmcStatus} onChange={e => setForm(f => ({ ...f, nmcStatus: e.target.value }))} className="w-full px-4 py-2 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-theme-primary">
                                        <option value="">Select Status</option>
                                        <option value="Approved">Approved</option>
                                        <option value="Pending">Pending</option>
                                        <option value="Not Approved">Not Approved</option>
                                    </select>
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Gallery Images</label>
                                <input type="file" multiple accept="image/*" onChange={handleGalleryUpload} className="w-full text-sm text-theme-secondary file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary-blue/10 file:text-primary-blue hover:file:bg-primary-blue/20" />
                                <div className="flex gap-2 mt-2 overflow-x-auto pb-2">
                                    {form.galleryImages.map((src, i) => (
                                        <div key={i} className="relative h-16 w-16 shrink-0 rounded-lg overflow-hidden group">
                                            <img src={src} className="h-full w-full object-cover" alt="" />
                                            <button type="button" onClick={() => setForm(f => ({ ...f, galleryImages: f.galleryImages.filter((_, idx) => idx !== i) }))} className="absolute top-0 right-0 p-0.5 bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity"><X className="h-3 w-3" /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-theme-secondary mb-1">Brochure (PDF)</label>
                                <div className="flex items-center gap-2">
                                    <input type="file" accept=".pdf" onChange={handleBrochureUpload} className="flex-1 text-sm text-theme-secondary file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-xs file:font-semibold file:bg-primary-blue/10 file:text-primary-blue hover:file:bg-primary-blue/20" />
                                    {form.brochureUrl && <span className="text-sm text-green-600 font-medium">Uploaded</span>}
                                </div>
                            </div>

                            <label className="flex items-center gap-2">
                                <input type="checkbox" checked={form.isActive} onChange={e => setForm(f => ({ ...f, isActive: e.target.checked }))} className="rounded border-slate-300" />
                                <span className="text-sm text-theme-secondary">Visible on public page</span>
                            </label>
                            <div className="flex gap-2 pt-2">
                                <button type="button" onClick={() => setModalOpen(false)} className="flex-1 py-2 rounded-lg border border-slate-300 dark:border-slate-600 text-theme-primary">Cancel</button>
                                <button type="submit" className="flex-1 py-2 rounded-lg bg-primary-blue text-white font-semibold">Save</button>
                            </div>
                        </form>
                    </GlassmorphicCard>
                </div>
            )}
        </div>
    )
}
